import React from 'react';
import { Check, ChefHat, BellRing, Sparkles, ArrowRight, AlertCircle } from 'lucide-react';

/**
 * Top-of-menu banner that surfaces the customer's current active order
 * without forcing them to open the cart drawer. Auto-hides 30 minutes after
 * completion (handled server-side via /api/orders + /api/orders/guest). Tap
 * to open the cart drawer.
 *
 * Props:
 *   orders: ActiveOrder[] — active + recently-completed (last 30 min)
 *   onOpenCart: () => void
 */

// Customer-facing 2-state aggregate: items are either "received" (chef has
// it, customer doesn't need granular updates) or "served" (delivered to the
// table). The 4-stage internal flow (received/preparing/ready/served) still
// drives the staff panels — this collapse is purely cosmetic for the customer.
function aggregateItemStatus(order) {
  const items = order.items || [];
  if (order.order_status === 'completed') return 'served';
  // If EVERY item is delivered, show served. Otherwise stay on received.
  const allServed = items.length > 0 && items.every(it => it.status === 'served' || it.status === 'completed');
  return allServed ? 'served' : 'received';
}

// Map an order's items to the relevant team that's handling it. Activities
// (snooker/pool) don't go to the kitchen — they go to the floor staff who
// set the table up. Tailoring the copy avoids customer confusion like
// "I ordered pool, why does it say 'sent to the kitchen'?".
function dominantCategory(items) {
  const cats = new Set();
  for (const it of items || []) {
    const c = it.category || it.prep_panel || '';
    if (c === 'food' || c === 'kitchen') cats.add('food');
    else if (c === 'drinks' || c === 'bar' || c === 'bartender') cats.add('drinks');
    else if (c === 'shisha') cats.add('shisha');
    else if (c === 'activities') cats.add('activities');
    else cats.add('other');
  }
  if (cats.size === 1) return [...cats][0];
  return 'mixed';
}

// (status, category) -> friendly sub-text. Keeps the main label generic
// ("Order placed", "Being prepared", ...) and varies only the explanation
// so the customer sees the right team mentioned.
const SUB_BY_CAT = {
  received: {
    food:       'Sent to the kitchen — sit tight.',
    drinks:     'Sent to the bar — sit tight.',
    shisha:     'Sent to our shisha team — coal is on its way.',
    activities: 'A staff member will set up your table shortly.',
    mixed:      'Sent to our team — sit tight.',
    other:      'Sent to our team — sit tight.',
  },
  preparing: {
    food:       'The kitchen is plating it now.',
    drinks:     'The bar is pouring it now.',
    shisha:     'Your shisha is being prepared.',
    activities: 'Setting up your table now.',
    mixed:      'Our team is on it — drinks and food on the way.',
    other:      'Our team is on it.',
  },
  ready: {
    food:       'A staff member will bring it over shortly.',
    drinks:     'A staff member will bring it over shortly.',
    shisha:     'Your shisha is on its way to your table.',
    activities: 'Your table is ready — head on over.',
    mixed:      'A staff member will bring it over shortly.',
    other:      'A staff member will bring it over shortly.',
  },
  served: {
    food:       'Delivered — tap to add more.',
    drinks:     'Delivered — tap to add more.',
    shisha:     'Enjoy your shisha. Tap to add more.',
    activities: 'Have fun! Tap to add drinks or food.',
    mixed:      'Delivered — tap to add more or call service.',
    other:      'Delivered — tap to add more or call service.',
  },
};

const STATUS_CFG = {
  received:  { icon: Check,    label: 'Order placed',  cls: 'border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300', iconCls: 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400' },
  preparing: { icon: ChefHat,  label: 'Being prepared',cls: 'border-amber-500/30  bg-amber-500/[0.06]  text-amber-300',     iconCls: 'bg-amber-500/15  border-amber-500/30  text-amber-400'  },
  ready:     { icon: BellRing, label: 'Ready for you', cls: 'border-blue-accent/30 bg-blue-accent/[0.08] text-blue-accent', iconCls: 'bg-blue-accent/15 border-blue-accent/30 text-blue-accent' },
  served:    { icon: Sparkles, label: 'Enjoy!',        cls: 'border-gold/30 bg-gold/[0.06] text-gold',                      iconCls: 'bg-gold/15 border-gold/30 text-gold' },
  // Payment-incomplete state: the order exists server-side but Apple Pay /
  // Square checkout was abandoned. We DON'T want the customer thinking the
  // bar already received it — they need to tap to finish paying first.
  payment_incomplete: {
    icon: AlertCircle,
    label: 'Payment incomplete',
    cls: 'border-red-500/40 bg-red-500/[0.08] text-red-300',
    iconCls: 'bg-red-500/15 border-red-500/40 text-red-300',
  },
};

export default function ActiveOrderBanner({ orders, onOpenCart }) {
  if (!orders || orders.length === 0) return null;
  // Show the most recent active order. If a customer has multiple, the cart
  // drawer shows all — the banner is just a glance-card for the latest one.
  const order = orders[0];
  // Payment-incomplete override: when Apple Pay / Square checkout was
  // abandoned the kitchen has NOT received the order — we must NOT say
  // "Sent to the bar". Override to a red "Payment incomplete · tap to retry"
  // banner so the customer notices and finishes paying.
  const paymentIncomplete =
    order.payment_status === 'pending' &&
    ['apple_pay', 'card'].includes(order.payment_method) &&
    order.order_status === 'active';
  const status = paymentIncomplete ? 'payment_incomplete' : aggregateItemStatus(order);
  const cat = dominantCategory(order.items);
  const cfg = STATUS_CFG[status] || STATUS_CFG.received;
  const sub = paymentIncomplete
    ? (order.payment_method === 'apple_pay'
        ? 'Apple Pay didn\u2019t complete — tap to retry or pick another method.'
        : 'Card checkout didn\u2019t finish — tap to retry or pick another method.')
    : ((SUB_BY_CAT[status] && SUB_BY_CAT[status][cat]) || SUB_BY_CAT[status]?.mixed || '');
  const Icon = cfg.icon;
  const itemCount = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
  const total = (order.total || 0).toFixed(2);
  // Payment badge — three states the customer should see clearly:
  //   * "Paid"        (green) — settled cash/card/Apple Pay, money in.
  //   * "On tab"      (amber) — order sits on an open tab, NOT YET PAID.
  //                   This was previously rolled up into the green "Paid"
  //                   pill, which let customers think the bill was
  //                   settled when it wasn't (owner-reported June 2026
  //                   regression).
  //   * (no badge)    — payment is still pending (Apple Pay/Square not
  //                   yet completed, or cash awaiting collection).
  const paid    = order.payment_status === 'paid';
  const onTab   = order.payment_status === 'tab';

  return (
    <button
      onClick={onOpenCart}
      className={`mx-4 mt-3 w-[calc(100%-2rem)] rounded-sm border ${cfg.cls} p-3 flex items-center gap-3 text-left transition-colors hover:bg-opacity-80`}
      data-testid="active-order-banner"
      aria-label={`Your order is ${cfg.label}. Tap to view details.`}
    >
      <div className={`w-9 h-9 rounded-sm border flex items-center justify-center shrink-0 ${cfg.iconCls}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-sans font-semibold leading-tight" data-testid="active-order-banner-label">
            {cfg.label}
          </p>
          {paid && (
            <span className="text-[9px] uppercase tracking-[0.18em] bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-1.5 py-0.5 rounded-sm font-sans font-bold" data-testid="active-order-paid-pill">
              Paid
            </span>
          )}
          {onTab && (
            <span className="text-[9px] uppercase tracking-[0.18em] bg-amber-500/15 text-amber-300 border border-amber-500/40 px-1.5 py-0.5 rounded-sm font-sans font-bold" data-testid="active-order-on-tab-pill">
              On tab
            </span>
          )}
          <span className="text-[10px] font-mono text-zinc-500">#{order.id.slice(0, 6).toUpperCase()}</span>
        </div>
        <p className="text-[12px] text-zinc-400 font-sans mt-0.5 leading-snug" data-testid="active-order-banner-sub">
          {sub}
        </p>
        <p className="text-[11px] text-zinc-500 font-sans mt-1">
          {itemCount} item{itemCount === 1 ? '' : 's'} · ${total}
        </p>
      </div>
      <ArrowRight className="w-4 h-4 text-zinc-500 shrink-0" />
    </button>
  );
}
